import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'NiFormOtp',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ni-otp.component.html',
})
export class NiOtpComponent implements AfterViewInit, OnChanges {
  @Input() length = 6;
  @Input() value = '';
  @Input() showError = false;
  @Input() label : string = ''
  @Input() errorMessage = 'Invalid OTP';
  @Output() valueChange = new EventEmitter<string>();
  @Output() changed = new EventEmitter<string>();
  @Output() completed = new EventEmitter<string>();
  @Output() inputChange = new EventEmitter<string>();

  digits: string[] = [];

  @ViewChildren('otpInput') inputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor() {
    this.resetDigits();
  }

  trackByIndex(index: number): number {
  return index;
}


  ngAfterViewInit(): void {
    // autofocus first box on init
    const first = this.inputs.first;
    if (first) {
      first.nativeElement.focus();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['length'] && !changes['length'].firstChange) {
      this.resetDigits();
    }

    if (changes['value'] && !changes['value'].firstChange) {
      this.syncDigitsFromValue();
    }
  }

  private resetDigits(): void {
    this.digits = Array(this.length).fill('');
    this.syncDigitsFromValue();
  }

  private syncDigitsFromValue(): void {
    const val = (this.value || '').slice(0, this.length);
    this.digits = Array(this.length)
      .fill('')
      .map((_, i) => val[i] ?? '');
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let val = input.value.replace(/\D/g, '');

    if (!val) {
      this.digits[index] = '';
      this.emitValue();
      return;
    }

    val = val[0];
    this.digits[index] = val;
    input.value = val;

    this.emitValue();

    if (index < this.length - 1) {
      this.focusInput(index + 1);
    }
  }

  private focusInput(index: number): void {
    const el = this.inputs.get(index);
    if (el) {
      el.nativeElement.focus();
      el.nativeElement.select();
    }
  }

  private emitValue(): void {
    this.value = this.digits.join('');
    this.valueChange.emit(this.value);
    this.changed.emit(this.value);
    this.inputChange.emit(this.value);

    if (this.isComplete()) {
      this.completed.emit(this.value);
    }
  }

  private isComplete(): boolean {
    return this.value.length === this.length && !this.value.includes(' ');
  }

  onDigitChange(raw: string, index: number): void {
    if (raw == null) raw = '';

    // Keep only digits
    const digitsOnly = raw.replace(/\D/g, '');

    if (!digitsOnly) {
      this.digits[index] = '';
      this.emitValue();
      return;
    }

    // Only keep the last typed digit
    const digit = digitsOnly[digitsOnly.length - 1];
    this.digits[index] = digit;
    this.emitValue();

    // Move to next box
    if (index < this.length - 1) {
      this.focusInput(index + 1);
    } else if (this.isComplete()) {
      this.completed.emit(this.value);
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const key = event.key;

    if (key === 'Backspace') {
      event.preventDefault();

      if (this.digits[index]) {
        this.digits[index] = '';
        this.emitValue();
        return;
      }

      if (index > 0) {
        this.focusInput(index - 1);
        this.digits[index - 1] = '';
        this.emitValue();
      }
      return;
    }

    if (key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusInput(index - 1);
      return;
    }

    if (key === 'ArrowRight' && index < this.length - 1) {
      event.preventDefault();
      this.focusInput(index + 1);
      return;
    }

    if (key === 'Tab') return; // allow tab

    if (!/^\d$/.test(key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();

    const pasted = event.clipboardData?.getData('text') ?? '';
    const nums = pasted.replace(/\D/g, '');
    if (!nums) return;

    let cursor = index;
    for (let i = 0; i < nums.length && cursor < this.length; i++, cursor++) {
      this.digits[cursor] = nums[i];
    }

    this.emitValue();

    const last = Math.min(cursor - 1, this.length - 1);
    if (last >= 0) {
      this.focusInput(last);
    }

    if (this.isComplete()) {
      this.completed.emit(this.value);
    }
  }
}
